import withRouter from "Components/Common/withRouter"
import { AddressUI } from "models/address"
import { useCallback, useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { createSelector } from "reselect"
import { ColumnDef } from "@tanstack/react-table"
import useDataTable from "hooks/useDataTable"
import React from "react"
import { ClientSliceState, setClientCount, setClients, setQuery } from "slices/business-development/client/reducer"
import { getClients } from "slices/thunk"
import { ClientWithCounts, IClientQueryParams } from "models/client"
import { navigateToClientDetails } from "utils/navigator"
import DataTableTemplate from "Components/templates/data-table-template"
import SearchClientForm from "Components/organisms/forms/search-client-form"
import ActionButton from "Components/atoms/buttons/action-button"
import { HelpIcon } from "Components/atoms/icons"
import useManual from "hooks/useManual"

const selectClient = createSelector(
    (state: any) => state.Client,
    ({ clients, clientCount, query }: ClientSliceState) => ({
        clients,
        clientCount,
        query,
    })
)

const ClientPage = (props: any) => {
    document.title = "Clients | IbexIQ"
    const { handleOpenManual } = useManual("business-development/client");
    const [isLoadingClients, setIsLoadingClients] = useState<boolean>(true)
    const dispatch: any = useDispatch()

    const {
        clients,
        clientCount,
        query,
    } = useSelector(selectClient)

    const queryClients = useCallback(async (q: IClientQueryParams) => {
        setIsLoadingClients(true)
        const res = await getClients(q)
        if (res) {
            dispatch(setClients(res.data))
            dispatch(setClientCount(res.count))
        }
        setIsLoadingClients(false)
    }, [dispatch])

    const handleChangeQuery = (key: string, value: any) => {
        const q = { ...query }
        if (key !== 'page' && key !== 'limit') {
            q.page = 0
            q.limit = Number.MAX_SAFE_INTEGER
        }
        q[key] = value
        dispatch(setQuery(q))
    }

    const handleClearSearch = useCallback(() => {
        const q: IClientQueryParams = {
            ...query,
            page: 0,
            name: undefined,
        }
        dispatch(setQuery(q))
    }, [query, dispatch])

    const initializePage = async () => {

        if (clients.length === 0) {
            await queryClients(query)
        }
        setIsLoadingClients(false)
    }

    useEffect(() => {
        initializePage()
    }, [])

    const handleClickClient = (clientId: string) => {
        navigateToClientDetails(props.router.navigate, clientId)
    }

    const columns = React.useMemo<ColumnDef<ClientWithCounts>[]>(() => [
        {
            header: "Name",
            accessorKey: "name",
            size: 150,
            cell: ({ row }) => (
                <a
                    style={{ color: "var(--bs-table-link-color)", cursor: "pointer" }}
                    onClick={() => handleClickClient(row.original.id)}
                >
                    {row.original.name}
                </a>
            ),
        },
        {
            header: "Address",
            id: "address",
            size: 250,
            meta: {
                getHeaderClassName: () => 'text-end',
                getCellClassName: () => 'text-end',
            },
            accessorFn: (row) => AddressUI.build(row.address),
            cell: ({ row }) => AddressUI.build(row.original.address),
        },
        {
            header: "Projects",
            id: "projectCount",
            size: 75,
            meta: {
                getHeaderClassName: () => 'text-end',
                getCellClassName: () => 'text-end',
            },
            accessorFn: (row) => row.projectCount,
            cell: ({ row }) => row.original.projectCount,
        },
        {
            header: "Opportunities",
            id: "opportunityCount",
            size: 100,
            meta: {
                getHeaderClassName: () => 'text-end',
                getCellClassName: () => 'text-end',
            },
            accessorFn: (row) => row.opportunityCount,
            cell: ({ row }) => row.original.opportunityCount,
        },
    ], []);

    const handleSearch = useCallback(async () => {
        await queryClients(query)
    }, [query, queryClients])

    const table = useDataTable({
        data: clients,
        columns,
        columnResizeMode: 'onEnd',
        enableColumnResizing: true
    });

    return <DataTableTemplate
        title={"Business Development"}
        breadcrumbItem={"Clients"}
        table={table}
        columns={columns}
        isLoading={isLoadingClients}
        rowCount={clientCount}
        actions={<ActionButton onClick={handleOpenManual}>Help <HelpIcon /></ActionButton>}
        filters={
            <SearchClientForm
                handleClearSearch={handleClearSearch}
                query={query}
                handleChangeQuery={handleChangeQuery}
                handleSearch={handleSearch}
                isSearching={isLoadingClients}
            />
        }
    />
}

export default withRouter(ClientPage);
